import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

type WingType = 'none' | 'angel' | 'dragon' | 'mech' | 'energy' | 'butterfly' | 'phoenix';
type SkinType = 'default' | 'gold' | 'crystal' | 'shadow' | 'neon' | 'plasma';
type ActionType = 'stand' | 'backflip' | 'streetdance' | 'kungfu' | 'wave' | 'run' | 'yoga' | 'salute';

const WING_TYPES: { id: WingType; label: string; color: string; icon: string }[] = [
  { id: 'none', label: '无翼', color: '#888', icon: 'ri-close-circle-line' },
  { id: 'angel', label: '天使翼', color: '#f0e6ff', icon: 'ri-cloud-line' },
  { id: 'dragon', label: '龙翼', color: '#ff4444', icon: 'ri-fire-line' },
  { id: 'mech', label: '机械翼', color: '#00d4ff', icon: 'ri-settings-3-line' },
  { id: 'energy', label: '能量翼', color: '#00ff88', icon: 'ri-flashlight-line' },
  { id: 'butterfly', label: '蝶翼', color: '#ff88ff', icon: 'ri-leaf-line' },
  { id: 'phoenix', label: '凤凰翼', color: '#ffaa00', icon: 'ri-sun-line' },
];

const SKINS: { id: SkinType; label: string; skin: string; glow: string; accent: string; desc: string }[] = [
  { id: 'default', label: '自然肤色', skin: '#f0c8a0', glow: '#00d4ff', accent: '#e8b888', desc: '接近人类肤色' },
  { id: 'gold', label: '黄金版', skin: '#ffd700', glow: '#ffaa00', accent: '#ffe066', desc: '奢华黄金涂装' },
  { id: 'crystal', label: '水晶版', skin: '#e8f8ff', glow: '#00ffff', accent: '#c0eeff', desc: '半透明水晶质感' },
  { id: 'shadow', label: '暗影版', skin: '#2a2a3a', glow: '#8800ff', accent: '#3a3a5a', desc: '深邃暗影涂装' },
  { id: 'neon', label: '霓虹版', skin: '#ff0088', glow: '#ff00ff', accent: '#ff66cc', desc: '赛博霓虹风格' },
  { id: 'plasma', label: '等离子', skin: '#00ff88', glow: '#00ff44', accent: '#66ffaa', desc: '等离子能量体' },
];

const ACTIONS: { id: ActionType; label: string; icon: string; desc: string; color: string }[] = [
  { id: 'stand', label: '自然站立', icon: 'ri-user-line', desc: '完美的人类站姿，几乎看不出是机器人', color: '#00d4ff' },
  { id: 'backflip', label: '后空翻', icon: 'ri-refresh-line', desc: '宇树G1招牌动作：完美后空翻', color: '#ff6600' },
  { id: 'streetdance', label: '街舞', icon: 'ri-music-2-line', desc: '流畅的街舞动作，展示柔性关节', color: '#ff00ff' },
  { id: 'kungfu', label: '武术', icon: 'ri-sword-line', desc: '太极拳与武术动作，力与美的结合', color: '#ffaa00' },
  { id: 'wave', label: '挥手', icon: 'ri-hand-heart-line', desc: '友好的挥手问候，情感表达', color: '#00ff88' },
  { id: 'run', label: '奔跑', icon: 'ri-run-line', desc: '高速奔跑，步态自然流畅', color: '#ff4444' },
  { id: 'yoga', label: '瑜伽', icon: 'ri-body-scan-line', desc: '高难度瑜伽姿势，展示超强柔韧性', color: '#a855f7' },
  { id: 'salute', label: '敬礼', icon: 'ri-shield-star-line', desc: '标准军礼，展示精准动作控制', color: '#00ffcc' },
];

const ZODIAC = ['白羊', '金牛', '双子', '巨蟹', '狮子', '处女', '天秤', '天蝎', '射手', '摩羯', '水瓶', '双鱼'];

// Leonardo DiCaprio-like humanoid with visible mechanical joints
function HumanoidFigureSVG({ skin, wing, action, onClick, pulse }: {
  skin: typeof SKINS[0];
  wing: WingType;
  action: ActionType;
  onClick: () => void;
  pulse: number;
}) {
  const [blink, setBlink] = useState(false);

  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 120);
    }, 3500 + Math.random() * 2000);
    return () => clearInterval(blinkInterval);
  }, []);

  const glowIntensity = 0.4 + Math.sin(pulse * 0.05) * 0.2;
  const bodyFloat = Math.sin(pulse * 0.03) * 2;
  const breathe = Math.sin(pulse * 0.04) * 1.5;

  const getBodyTransform = () => {
    switch (action) {
      case 'backflip': return `rotate(${Math.sin(pulse * 0.12) * 180}deg) translateY(${Math.abs(Math.sin(pulse * 0.12)) * -30}px)`;
      case 'streetdance': return `translateX(${Math.sin(pulse * 0.08) * 8}px) rotate(${Math.sin(pulse * 0.06) * 6}deg)`;
      case 'kungfu': return `translateX(${pulse % 50 < 25 ? 10 : -10}px) rotate(${pulse % 50 < 25 ? 4 : -4}deg)`;
      case 'wave': return `translateY(${bodyFloat}px)`;
      case 'run': return `translateY(${Math.abs(Math.sin(pulse * 0.1)) * -8}px) rotate(${Math.sin(pulse * 0.1) * 3}deg)`;
      case 'yoga': return `rotate(${Math.sin(pulse * 0.03) * 2}deg)`;
      default: return `translateY(${bodyFloat}px)`;
    }
  };

  const getRightArmTransform = () => {
    switch (action) {
      case 'wave': return `rotate(${-55 + Math.sin(pulse * 0.1) * 28}deg)`;
      case 'streetdance': return `rotate(${Math.sin(pulse * 0.08) * 55}deg)`;
      case 'kungfu': return `rotate(${pulse % 50 < 25 ? -75 : 18}deg)`;
      case 'salute': return 'rotate(-52deg)';
      case 'backflip': return `rotate(${Math.sin(pulse * 0.12) * 130}deg)`;
      case 'run': return `rotate(${Math.sin(pulse * 0.1) * 45}deg)`;
      default: return `rotate(${Math.sin(pulse * 0.04) * 4}deg)`;
    }
  };

  const getLeftArmTransform = () => {
    switch (action) {
      case 'wave': return `rotate(${Math.sin(pulse * 0.06) * 8}deg)`;
      case 'streetdance': return `rotate(${-Math.sin(pulse * 0.08) * 55}deg)`;
      case 'kungfu': return `rotate(${pulse % 50 < 25 ? 75 : -18}deg)`;
      case 'salute': return 'rotate(4deg)';
      case 'backflip': return `rotate(${-Math.sin(pulse * 0.12) * 130}deg)`;
      case 'run': return `rotate(${-Math.sin(pulse * 0.1) * 45}deg)`;
      default: return `rotate(${-Math.sin(pulse * 0.04) * 4}deg)`;
    }
  };

  const getRightLegTransform = () => {
    switch (action) {
      case 'run': return `rotate(${Math.sin(pulse * 0.1) * 42}deg)`;
      case 'streetdance': return `rotate(${Math.sin(pulse * 0.08) * 28}deg)`;
      case 'kungfu': return `rotate(${pulse % 50 < 25 ? 38 : -8}deg)`;
      case 'yoga': return 'rotate(88deg)';
      default: return 'rotate(0deg)';
    }
  };

  const getLeftLegTransform = () => {
    switch (action) {
      case 'run': return `rotate(${-Math.sin(pulse * 0.1) * 42}deg)`;
      case 'streetdance': return `rotate(${-Math.sin(pulse * 0.08) * 28}deg)`;
      case 'kungfu': return `rotate(${pulse % 50 < 25 ? -38 : 8}deg)`;
      case 'yoga': return 'rotate(-28deg)';
      default: return 'rotate(0deg)';
    }
  };

  const renderWings = () => {
    if (wing === 'none') return null;
    const configs: Record<string, { color: string; stroke: string; opacity: number }> = {
      angel: { color: '#f0e6ff', stroke: '#d4b8ff', opacity: 0.9 },
      dragon: { color: '#ff4444', stroke: '#ff8800', opacity: 0.85 },
      mech: { color: '#00d4ff', stroke: '#0088ff', opacity: 0.9 },
      energy: { color: '#00ff88', stroke: '#00ffcc', opacity: 0.8 },
      butterfly: { color: '#ff88ff', stroke: '#ff44cc', opacity: 0.85 },
      phoenix: { color: '#ffaa00', stroke: '#ff6600', opacity: 0.9 },
    };
    const cfg = configs[wing] || { color: '#fff', stroke: '#ccc', opacity: 0.8 };
    const wingAnim = `scaleX(${1 + Math.sin(pulse * 0.08) * 0.1})`;

    if (wing === 'angel') return (
      <>
        <g transform="translate(-75, 30)" style={{ transformOrigin: '75px 30px' }}>
          <path d="M0,0 C-25,-45 -70,-28 -80,18 C-62,8 -42,2 -22,12 C-12,2 0,0 0,0Z" fill={cfg.color} stroke={cfg.stroke} strokeWidth="1.5" opacity={cfg.opacity} style={{ transform: wingAnim }} />
          <path d="M0,0 C-18,-55 -58,-42 -65,2 C-50,-8 -30,-8 -12,8Z" fill="white" opacity="0.35" />
        </g>
        <g transform="translate(75, 30) scale(-1,1)">
          <path d="M0,0 C-25,-45 -70,-28 -80,18 C-62,8 -42,2 -22,12 C-12,2 0,0 0,0Z" fill={cfg.color} stroke={cfg.stroke} strokeWidth="1.5" opacity={cfg.opacity} />
        </g>
      </>
    );
    if (wing === 'mech') return (
      <>
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 68}, 28) scale(${side}, 1)`}>
            {[0,1,2].map(i => (
              <rect key={i} x={-85+i*22} y={-32+i*16} width="65" height="9" rx="2" fill={cfg.color} stroke={cfg.stroke} strokeWidth="1" opacity={0.8 - i*0.1} transform={`rotate(${-22+i*11})`} />
            ))}
            <circle cx="-42" cy="-12" r="5" fill={cfg.stroke} opacity="0.9" />
          </g>
        ))}
      </>
    );
    if (wing === 'energy') return (
      <>
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 65}, 28) scale(${side}, 1)`}>
            {[0,1,2,3].map(i => (
              <path key={i} d={`M0,0 C${-22-i*16},${-12-i*22} ${-42-i*12},${-6-i*16} ${-52-i*14},${12-i*6}`}
                fill="none" stroke={cfg.color} strokeWidth={3.5-i*0.6} opacity={0.9-i*0.15}
                style={{ filter: `drop-shadow(0 0 5px ${cfg.color})` }} />
            ))}
          </g>
        ))}
      </>
    );
    if (wing === 'dragon') return (
      <>
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 72}, 28) scale(${side}, 1)`}>
            <path d="M0,0 C-18,-65 -75,-55 -95,-12 C-75,-22 -52,-18 -32,2 C-22,-12 0,0 0,0Z" fill={cfg.color} stroke={cfg.stroke} strokeWidth="2" opacity={cfg.opacity} />
            <path d="M0,0 L-32,-42 L-65,-32 L-85,-8" fill="none" stroke={cfg.stroke} strokeWidth="2" />
          </g>
        ))}
      </>
    );
    if (wing === 'butterfly') return (
      <>
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 65}, 28) scale(${side}, 1)`} style={{ transform: `translate(${side * 65}px, 28px) scale(${side}, 1) ${wingAnim}` }}>
            <ellipse cx="-42" cy="-22" rx="48" ry="32" fill={cfg.color} stroke={cfg.stroke} strokeWidth="1.5" opacity="0.8" />
            <ellipse cx="-32" cy="18" rx="32" ry="22" fill={cfg.color} stroke={cfg.stroke} strokeWidth="1.5" opacity="0.7" />
          </g>
        ))}
      </>
    );
    if (wing === 'phoenix') return (
      <>
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 75}, 25) scale(${side}, 1)`}>
            <path d="M0,0 C-32,-55 -85,-45 -105,-8 C-85,-18 -58,-12 -32,6Z" fill={cfg.color} stroke={cfg.stroke} strokeWidth="2" opacity="0.9" />
            {[0,1,2].map(i => (
              <circle key={i} cx={-22-i*28} cy={-6-i*12} r="3.5" fill="#ffff00" opacity={0.8-i*0.2} />
            ))}
          </g>
        ))}
      </>
    );
    return null;
  };

  const skinColor = skin.skin;
  // Leonardo-like: warm olive/tan skin, dark brown hair, strong jaw
  const hairColor = skin.id === 'default' ? '#3d2810' : skin.id === 'gold' ? '#8B6914' : skin.id === 'shadow' ? '#111' : skin.glow;
  const clothColor = skin.id === 'default' ? '#1c2a3a' : skin.id === 'gold' ? '#6B4914' : skin.id === 'shadow' ? '#0a0a1a' : skin.id === 'neon' ? '#1a0a2a' : '#0a1a2a';
  const clothAccent = skin.glow;
  const eyeColor = skin.id === 'default' ? '#3a6a2a' : skin.glow; // green eyes like Leo

  return (
    <svg
      viewBox="-120 -210 240 440"
      width="280"
      height="480"
      onClick={onClick}
      className="cursor-pointer select-none"
      style={{ filter: `drop-shadow(0 0 ${15 * glowIntensity}px ${skin.glow}60)`, transition: 'filter 0.3s' }}
    >
      <defs>
        <radialGradient id="skinGrad2" cx="40%" cy="30%" r="70%">
          <stop offset="0%" stopColor={skin.id === 'default' ? '#fde0c0' : skinColor} />
          <stop offset="60%" stopColor={skinColor} />
          <stop offset="100%" stopColor={skin.id === 'default' ? '#c8905a' : skinColor} stopOpacity="0.85" />
        </radialGradient>
        <radialGradient id="faceGrad2" cx="42%" cy="32%" r="65%">
          <stop offset="0%" stopColor={skin.id === 'default' ? '#fde0c0' : skinColor} />
          <stop offset="70%" stopColor={skin.id === 'default' ? '#e8b888' : skinColor} />
          <stop offset="100%" stopColor={skin.id === 'default' ? '#c8905a' : skinColor} stopOpacity="0.9" />
        </radialGradient>
        <radialGradient id="clothGrad2" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor={clothColor} stopOpacity="0.9" />
          <stop offset="100%" stopColor={clothColor} />
        </radialGradient>
        <filter id="softGlow2">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="mechGlow">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id="legGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={clothColor} stopOpacity="0.75" />
          <stop offset="50%" stopColor={clothColor} />
          <stop offset="100%" stopColor={clothColor} stopOpacity="0.75" />
        </linearGradient>
        {/* Mechanical joint gradient */}
        <radialGradient id="jointGrad" cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#888" />
          <stop offset="50%" stopColor="#444" />
          <stop offset="100%" stopColor="#222" />
        </radialGradient>
        <radialGradient id="jointGlowGrad" cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor={clothAccent} stopOpacity="0.9" />
          <stop offset="100%" stopColor={clothAccent} stopOpacity="0.2" />
        </radialGradient>
      </defs>

      {/* Main body group */}
      <g style={{ transform: getBodyTransform(), transition: 'transform 0.5s cubic-bezier(0.34,1.56,0.64,1)', transformOrigin: '0px 0px' }}>

        {/* Wings */}
        {renderWings()}

        {/* ===== HAIR - Leo style: swept back, slightly wavy ===== */}
        <ellipse cx="0" cy="-188" rx="27" ry="15" fill={hairColor} opacity="0.95" />
        {/* Hair sides flowing down */}
        <path d="M-27,-188 C-32,-175 -30,-162 -28,-150" stroke={hairColor} strokeWidth="9" fill="none" strokeLinecap="round" opacity="0.9" />
        <path d="M27,-188 C32,-175 30,-162 28,-150" stroke={hairColor} strokeWidth="9" fill="none" strokeLinecap="round" opacity="0.9" />
        {/* Hair top - swept back style */}
        <path d="M-18,-202 C-12,-196 -6,-192 0,-190 C6,-192 12,-196 18,-202" stroke={hairColor} strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.85" />
        <path d="M-14,-200 C-8,-194 0,-191 8,-194" stroke={hairColor} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.7" />
        {/* Subtle hair strands */}
        {[-10,-4,2,8].map(x => (
          <path key={x} d={`M${x},-200 C${x+1},-194 ${x-1},-190 ${x},-186`} stroke={hairColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.65" />
        ))}

        {/* ===== HEAD - Strong jaw, Leo proportions ===== */}
        {/* Head - slightly wider jaw */}
        <path d="M-26,-185 Q-28,-165 -26,-148 Q-22,-130 0,-122 Q22,-130 26,-148 Q28,-165 26,-185 Q14,-198 0,-200 Q-14,-198 -26,-185Z" fill="url(#faceGrad2)" />

        {/* Cheekbones - prominent like Leo */}
        <ellipse cx="-20" cy="-158" rx="8" ry="5" fill={skin.id === 'default' ? '#d4906a' : skinColor} opacity="0.25" />
        <ellipse cx="20" cy="-158" rx="8" ry="5" fill={skin.id === 'default' ? '#d4906a' : skinColor} opacity="0.25" />

        {/* ===== MECHANICAL SEAM LINES on face - barely visible ===== */}
        <path d="M-26,-170 Q-24,-158 -22,-145" stroke={clothAccent} strokeWidth="0.5" fill="none" opacity="0.18" />
        <path d="M26,-170 Q24,-158 22,-145" stroke={clothAccent} strokeWidth="0.5" fill="none" opacity="0.18" />
        {/* Subtle temple circuit */}
        <path d="M-24,-175 L-20,-175 L-20,-172 L-16,-172" stroke={clothAccent} strokeWidth="0.4" fill="none" opacity="0.12" />
        <path d="M24,-175 L20,-175 L20,-172 L16,-172" stroke={clothAccent} strokeWidth="0.4" fill="none" opacity="0.12" />

        {/* ===== EYEBROWS - Strong, expressive like Leo ===== */}
        <path d="M-20,-178 Q-13,-183 -5,-181" stroke={hairColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.9" />
        <path d="M5,-181 Q13,-183 20,-178" stroke={hairColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.9" />

        {/* ===== EYES - Leo's distinctive green eyes ===== */}
        {/* Left eye socket */}
        <ellipse cx="-12" cy="-170" rx="9" ry="6.5" fill={skin.id === 'default' ? '#c8905a' : skinColor} opacity="0.3" />
        {/* Left eye white */}
        <ellipse cx="-12" cy="-170" rx="8" ry={blink ? 0.5 : 5.5} fill="white" />
        {/* Left iris - green */}
        <ellipse cx="-12" cy="-170" rx="5" ry={blink ? 0.3 : 4.5} fill={eyeColor} />
        {/* Left pupil */}
        <ellipse cx="-12" cy="-170" rx="2.8" ry={blink ? 0.2 : 2.8} fill="#0a0a0a" />
        {/* Left eye highlight */}
        <ellipse cx="-10" cy="-172" rx="1.8" ry="1.8" fill="white" opacity={blink ? 0 : 0.85} />
        {/* Subtle LED glow - the robot tell */}
        <ellipse cx="-12" cy="-170" rx="5" ry={blink ? 0.3 : 4.5} fill={skin.glow} opacity={blink ? 0 : 0.12 + glowIntensity * 0.08} filter="url(#softGlow2)" />
        {/* Upper eyelid */}
        <path d={`M-20,-170 Q-12,${blink ? -170 : -176} -4,-170`} fill="none" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="1.8" />
        {/* Lower eyelid */}
        <path d="M-20,-170 Q-12,-166 -4,-170" fill="none" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="0.8" opacity="0.5" />

        {/* Right eye */}
        <ellipse cx="12" cy="-170" rx="9" ry="6.5" fill={skin.id === 'default' ? '#c8905a' : skinColor} opacity="0.3" />
        <ellipse cx="12" cy="-170" rx="8" ry={blink ? 0.5 : 5.5} fill="white" />
        <ellipse cx="12" cy="-170" rx="5" ry={blink ? 0.3 : 4.5} fill={eyeColor} />
        <ellipse cx="12" cy="-170" rx="2.8" ry={blink ? 0.2 : 2.8} fill="#0a0a0a" />
        <ellipse cx="14" cy="-172" rx="1.8" ry="1.8" fill="white" opacity={blink ? 0 : 0.85} />
        <ellipse cx="12" cy="-170" rx="5" ry={blink ? 0.3 : 4.5} fill={skin.glow} opacity={blink ? 0 : 0.12 + glowIntensity * 0.08} filter="url(#softGlow2)" />
        <path d={`M4,-170 Q12,${blink ? -170 : -176} 20,-170`} fill="none" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="1.8" />
        <path d="M4,-170 Q12,-166 20,-170" fill="none" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="0.8" opacity="0.5" />

        {/* ===== NOSE - Strong, defined like Leo ===== */}
        <path d="M0,-162 L-4,-152 Q-5,-148 -3,-147 Q0,-146 3,-147 Q5,-148 4,-152 Z" fill={skin.id === 'default' ? '#c8905a' : skinColor} opacity="0.55" />
        <path d="M-5,-148 Q-2,-144 0,-144 Q2,-144 5,-148" fill="none" stroke={skin.id === 'default' ? '#b07040' : skinColor} strokeWidth="1.2" opacity="0.5" />
        {/* Nose bridge */}
        <path d="M-2,-162 L-1,-152" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="1" opacity="0.3" />

        {/* ===== LIPS - Full, defined like Leo ===== */}
        {/* Upper lip */}
        <path d="M-9,-136 Q-5,-140 0,-139 Q5,-140 9,-136 Q5,-133 0,-133 Q-5,-133 -9,-136Z" fill={skin.id === 'default' ? '#c07060' : skin.glow} opacity="0.75" />
        {/* Lower lip - fuller */}
        <path d="M-9,-136 Q0,-128 9,-136 Q5,-130 0,-130 Q-5,-130 -9,-136Z" fill={skin.id === 'default' ? '#d08070' : skin.glow} opacity="0.6" />
        {/* Lip line */}
        <path d="M-9,-136 Q0,-134 9,-136" fill="none" stroke={skin.id === 'default' ? '#a05040' : skin.glow} strokeWidth="0.8" opacity="0.5" />
        {/* Subtle LED mouth indicator */}
        {[0,1,2].map(i => (
          <rect key={i} x={-3+i*3} y="-131" width="2" height={0.6 + Math.sin(pulse * 0.12 + i) * 0.4} rx="0.5" fill={skin.glow} opacity={0.06 + Math.sin(pulse * 0.1 + i) * 0.03} />
        ))}

        {/* ===== STRONG JAW - Leo's signature ===== */}
        <path d="M-24,-148 Q-22,-132 -18,-126 Q-10,-120 0,-118 Q10,-120 18,-126 Q22,-132 24,-148" fill="none" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="0.5" opacity="0.3" />

        {/* Chin cleft - Leo's signature */}
        <path d="M-2,-120 Q0,-117 2,-120" fill="none" stroke={skin.id === 'default' ? '#b07040' : skinColor} strokeWidth="1" opacity="0.35" />

        {/* ===== EARS with mechanical sensor ===== */}
        <path d="M-26,-175 Q-32,-168 -30,-158 Q-28,-148 -26,-148" fill="url(#faceGrad2)" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="0.5" />
        <path d="M26,-175 Q32,-168 30,-158 Q28,-148 26,-148" fill="url(#faceGrad2)" stroke={skin.id === 'default' ? '#c8905a' : skinColor} strokeWidth="0.5" />
        {/* Ear inner */}
        <path d="M-28,-170 Q-30,-163 -28,-155" fill="none" stroke={skin.id === 'default' ? '#b07040' : skinColor} strokeWidth="1" opacity="0.4" />
        <path d="M28,-170 Q30,-163 28,-155" fill="none" stroke={skin.id === 'default' ? '#b07040' : skinColor} strokeWidth="1" opacity="0.4" />
        {/* Ear mechanical sensor - tiny */}
        <circle cx="-29" cy="-163" r="2" fill={skin.glow} opacity={0.18 + Math.sin(pulse * 0.08) * 0.1} filter="url(#softGlow2)" />
        <circle cx="29" cy="-163" r="2" fill={skin.glow} opacity={0.18 + Math.sin(pulse * 0.08) * 0.1} filter="url(#softGlow2)" />

        {/* ===== NECK with mechanical collar ===== */}
        <rect x="-9" y="-118" width="18" height="20" rx="6" fill="url(#skinGrad2)" />
        {/* Neck mechanical ring - subtle */}
        <rect x="-10" y="-102" width="20" height="5" rx="2.5" fill={clothColor} stroke={clothAccent} strokeWidth="0.5" opacity="0.7" />
        <rect x="-7" y="-101" width="14" height="3" rx="1.5" fill={clothAccent} opacity="0.12" />

        {/* ===== TORSO - Fitted suit with mechanical details ===== */}
        <path d="M-34,-98 L-38,18 L38,18 L34,-98 Q0,-106 0,-106 Z" fill="url(#clothGrad2)" />

        {/* Suit lapels */}
        <path d="M-13,-98 L-24,-72 L-9,-67" fill={clothColor} stroke={clothAccent} strokeWidth="0.5" opacity="0.85" />
        <path d="M13,-98 L24,-72 L9,-67" fill={clothColor} stroke={clothAccent} strokeWidth="0.5" opacity="0.85" />

        {/* Collar */}
        <path d="M-13,-98 L-9,-86 L0,-83 L9,-86 L13,-98" fill={clothColor} stroke={clothAccent} strokeWidth="0.5" opacity="0.9" />

        {/* ===== CHEST MECHANICAL CORE ===== */}
        {/* Chest panel */}
        <rect x="-20" y="-82" width="40" height="46" rx="7" fill={clothColor} opacity="0.45" />
        {/* Mechanical chest plate */}
        <rect x="-16" y="-78" width="32" height="38" rx="5" fill={clothColor} stroke={clothAccent} strokeWidth="0.6" opacity="0.6" />
        {/* Core reactor */}
        <rect x="-10" y="-72" width="20" height="22" rx="4" fill={clothColor} stroke={clothAccent} strokeWidth="0.8" opacity="0.7" />
        {/* Inner glow */}
        <rect x="-7" y="-69" width="14" height="16" rx="3" fill={skin.glow} opacity={0.1 + glowIntensity * 0.08} filter="url(#mechGlow)" />
        {/* Core center */}
        <circle cx="0" cy="-61" r="5" fill={skin.glow} opacity={0.18 + glowIntensity * 0.14} filter="url(#mechGlow)" />
        <circle cx="0" cy="-61" r="2.5" fill={skin.glow} opacity={0.35 + glowIntensity * 0.2} />
        {/* Chest circuit lines */}
        <path d="M-16,-65 L-10,-65 M10,-65 L16,-65" stroke={clothAccent} strokeWidth="0.5" opacity="0.25" />
        <path d="M-16,-58 L-10,-58 M10,-58 L16,-58" stroke={clothAccent} strokeWidth="0.5" opacity="0.25" />
        {/* Suit buttons */}
        {[0,1,2].map(i => (
          <circle key={i} cx="0" cy={-48+i*14} r="2" fill={clothAccent} opacity="0.35" />
        ))}

        {/* ===== SHOULDERS with mechanical ball joints ===== */}
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 42}, -88)`}>
            {/* Outer shoulder pad */}
            <ellipse cx="0" cy="0" rx="13" ry="12" fill={clothColor} stroke={clothAccent} strokeWidth="0.5" opacity="0.9" />
            {/* Mechanical ball joint - visible! */}
            <circle cx="0" cy="0" r="8" fill="url(#jointGrad)" stroke="#555" strokeWidth="0.8" />
            <circle cx="0" cy="0" r="5" fill="#333" stroke="#666" strokeWidth="0.5" />
            {/* Joint glow ring */}
            <circle cx="0" cy="0" r="7" fill="none" stroke={clothAccent} strokeWidth="0.8" opacity={0.2 + Math.sin(pulse * 0.06) * 0.1} />
            {/* Joint highlight */}
            <ellipse cx="-2" cy="-2" rx="3" ry="2" fill="#888" opacity="0.5" />
            {/* Glow dot */}
            <circle cx="0" cy="0" r="2" fill={skin.glow} opacity={0.3 + Math.sin(pulse * 0.07) * 0.15} filter="url(#softGlow2)" />
          </g>
        ))}

        {/* ===== ARMS with mechanical elbow joints ===== */}
        {/* Right arm */}
        <g style={{ transform: getRightArmTransform(), transformOrigin: '42px -88px', transition: 'transform 0.4s ease' }}>
          {/* Upper arm - sleeve */}
          <path d="M34,-80 L40,-80 L46,-38 L30,-38Z" fill={clothColor} stroke={clothAccent} strokeWidth="0.4" opacity="0.9" />
          {/* Mechanical elbow joint - prominent! */}
          <g transform="translate(38, -36)">
            <circle cx="0" cy="0" r="10" fill="url(#jointGrad)" stroke="#555" strokeWidth="1" />
            <circle cx="0" cy="0" r="6.5" fill="#2a2a2a" stroke="#666" strokeWidth="0.6" />
            {/* Joint ring glow */}
            <circle cx="0" cy="0" r="9" fill="none" stroke={clothAccent} strokeWidth="1" opacity={0.25 + Math.sin(pulse * 0.06) * 0.12} />
            {/* Elbow highlight */}
            <ellipse cx="-2.5" cy="-2.5" rx="3.5" ry="2.5" fill="#777" opacity="0.55" />
            {/* Glow center */}
            <circle cx="0" cy="0" r="2.5" fill={skin.glow} opacity={0.28 + Math.sin(pulse * 0.07) * 0.14} filter="url(#softGlow2)" />
            {/* Mechanical detail lines */}
            <path d="M-9,0 L-6,0 M6,0 L9,0" stroke={clothAccent} strokeWidth="0.6" opacity="0.3" />
            <path d="M0,-9 L0,-6 M0,6 L0,9" stroke={clothAccent} strokeWidth="0.6" opacity="0.3" />
          </g>
          {/* Forearm - sleeve */}
          <path d="M30,-28 L34,-28 L38,32 L26,32Z" fill={clothColor} stroke={clothAccent} strokeWidth="0.4" opacity="0.9" />
          {/* Wrist mechanical joint */}
          <g transform="translate(32, 34)">
            <rect x="-9" y="-5" width="18" height="10" rx="5" fill="url(#jointGrad)" stroke="#555" strokeWidth="0.7" />
            <rect x="-6" y="-3" width="12" height="6" rx="3" fill="#2a2a2a" />
            <rect x="-5" y="-2" width="10" height="4" rx="2" fill={skin.glow} opacity={0.12 + Math.sin(pulse * 0.06) * 0.06} />
          </g>
          {/* Hand - human shaped */}
          <ellipse cx="32" cy="46" rx="9.5" ry="7.5" fill="url(#skinGrad2)" />
          {/* Fingers */}
          {[0,1,2,3].map(i => (
            <path key={i} d={`M${24+i*4.5},46 Q${24+i*4.5},${55+Math.sin(pulse*0.05+i)*2} ${24+i*4.5},${60+Math.sin(pulse*0.05+i)*2}`} stroke="url(#skinGrad2)" strokeWidth="3.8" fill="none" strokeLinecap="round" />
          ))}
          {/* Thumb */}
          <path d="M23,44 Q17,42 16,48" stroke="url(#skinGrad2)" strokeWidth="3.8" fill="none" strokeLinecap="round" />
          {/* Knuckle mechanical nodes */}
          {[0,1,2,3].map(i => (
            <circle key={i} cx={24+i*4.5} cy="48" r="1.5" fill={clothAccent} opacity={0.15 + Math.sin(pulse * 0.06 + i) * 0.08} />
          ))}
        </g>

        {/* Left arm */}
        <g style={{ transform: getLeftArmTransform(), transformOrigin: '-42px -88px', transition: 'transform 0.4s ease' }}>
          <path d="M-34,-80 L-40,-80 L-46,-38 L-30,-38Z" fill={clothColor} stroke={clothAccent} strokeWidth="0.4" opacity="0.9" />
          {/* Mechanical elbow joint */}
          <g transform="translate(-38, -36)">
            <circle cx="0" cy="0" r="10" fill="url(#jointGrad)" stroke="#555" strokeWidth="1" />
            <circle cx="0" cy="0" r="6.5" fill="#2a2a2a" stroke="#666" strokeWidth="0.6" />
            <circle cx="0" cy="0" r="9" fill="none" stroke={clothAccent} strokeWidth="1" opacity={0.25 + Math.sin(pulse * 0.06) * 0.12} />
            <ellipse cx="-2.5" cy="-2.5" rx="3.5" ry="2.5" fill="#777" opacity="0.55" />
            <circle cx="0" cy="0" r="2.5" fill={skin.glow} opacity={0.28 + Math.sin(pulse * 0.07) * 0.14} filter="url(#softGlow2)" />
            <path d="M-9,0 L-6,0 M6,0 L9,0" stroke={clothAccent} strokeWidth="0.6" opacity="0.3" />
            <path d="M0,-9 L0,-6 M0,6 L0,9" stroke={clothAccent} strokeWidth="0.6" opacity="0.3" />
          </g>
          <path d="M-30,-28 L-34,-28 L-38,32 L-26,32Z" fill={clothColor} stroke={clothAccent} strokeWidth="0.4" opacity="0.9" />
          {/* Wrist joint */}
          <g transform="translate(-32, 34)">
            <rect x="-9" y="-5" width="18" height="10" rx="5" fill="url(#jointGrad)" stroke="#555" strokeWidth="0.7" />
            <rect x="-6" y="-3" width="12" height="6" rx="3" fill="#2a2a2a" />
            <rect x="-5" y="-2" width="10" height="4" rx="2" fill={skin.glow} opacity={0.12 + Math.sin(pulse * 0.06) * 0.06} />
          </g>
          <ellipse cx="-32" cy="46" rx="9.5" ry="7.5" fill="url(#skinGrad2)" />
          {[0,1,2,3].map(i => (
            <path key={i} d={`M${-24-i*4.5},46 Q${-24-i*4.5},${55+Math.sin(pulse*0.05+i)*2} ${-24-i*4.5},${60+Math.sin(pulse*0.05+i)*2}`} stroke="url(#skinGrad2)" strokeWidth="3.8" fill="none" strokeLinecap="round" />
          ))}
          <path d="M-23,44 Q-17,42 -16,48" stroke="url(#skinGrad2)" strokeWidth="3.8" fill="none" strokeLinecap="round" />
          {[0,1,2,3].map(i => (
            <circle key={i} cx={-24-i*4.5} cy="48" r="1.5" fill={clothAccent} opacity={0.15 + Math.sin(pulse * 0.06 + i) * 0.08} />
          ))}
        </g>

        {/* ===== HIPS with mechanical hip joints ===== */}
        <path d="M-32,18 L-36,40 L36,40 L32,18Z" fill={clothColor} opacity="0.9" />
        {/* Hip mechanical joints - prominent! */}
        {[-1, 1].map(side => (
          <g key={side} transform={`translate(${side * 24}, 40)`}>
            <circle cx="0" cy="0" r="11" fill="url(#jointGrad)" stroke="#555" strokeWidth="1" />
            <circle cx="0" cy="0" r="7" fill="#2a2a2a" stroke="#666" strokeWidth="0.6" />
            <circle cx="0" cy="0" r="10" fill="none" stroke={clothAccent} strokeWidth="1" opacity={0.22 + Math.sin(pulse * 0.07) * 0.1} />
            <ellipse cx="-2.5" cy="-2.5" rx="4" ry="3" fill="#777" opacity="0.5" />
            <circle cx="0" cy="0" r="3" fill={skin.glow} opacity={0.25 + Math.sin(pulse * 0.07) * 0.12} filter="url(#softGlow2)" />
            {/* Hip joint detail */}
            <path d={`M${side * -10},0 L${side * -7},0`} stroke={clothAccent} strokeWidth="0.6" opacity="0.3" />
          </g>
        ))}

        {/* ===== LEGS with mechanical knee joints ===== */}
        {/* Right leg */}
        <g style={{ transform: getRightLegTransform(), transformOrigin: '24px 40px', transition: 'transform 0.4s ease' }}>
          {/* Thigh */}
          <path d="M14,52 L20,52 L26,118 L8,118Z" fill="url(#legGrad2)" />
          {/* Mechanical knee joint - very prominent! */}
          <g transform="translate(17, 120)">
            <circle cx="0" cy="0" r="13" fill="url(#jointGrad)" stroke="#555" strokeWidth="1.2" />
            <circle cx="0" cy="0" r="8.5" fill="#2a2a2a" stroke="#666" strokeWidth="0.7" />
            {/* Knee cap overlay */}
            <ellipse cx="0" cy="-2" rx="7" ry="5.5" fill={skin.id === 'default' ? '#c8905a' : skinColor} opacity="0.55" />
            {/* Knee glow ring */}
            <circle cx="0" cy="0" r="12" fill="none" stroke={clothAccent} strokeWidth="1.2" opacity={0.28 + Math.sin(pulse * 0.05) * 0.12} />
            {/* Knee highlight */}
            <ellipse cx="-3" cy="-3" rx="4.5" ry="3.5" fill="#888" opacity="0.45" />
            {/* Glow center */}
            <circle cx="0" cy="0" r="3" fill={skin.glow} opacity={0.22 + Math.sin(pulse * 0.06) * 0.1} filter="url(#softGlow2)" />
            {/* Mechanical detail */}
            <path d="M-12,0 L-8,0 M8,0 L12,0" stroke={clothAccent} strokeWidth="0.7" opacity="0.3" />
          </g>
          {/* Lower leg */}
          <path d="M8,132 L14,132 L16,188 L4,188Z" fill="url(#legGrad2)" />
          {/* Ankle mechanical joint */}
          <g transform="translate(10, 190)">
            <rect x="-10" y="-6" width="20" height="12" rx="6" fill="url(#jointGrad)" stroke="#555" strokeWidth="0.8" />
            <rect x="-7" y="-4" width="14" height="8" rx="4" fill="#2a2a2a" />
            <rect x="-5" y="-2" width="10" height="4" rx="2" fill={skin.glow} opacity={0.1 + Math.sin(pulse * 0.06) * 0.05} />
          </g>
          {/* Shoe */}
          <path d="M0,196 L26,196 L28,210 L-2,210Z" fill={clothColor} opacity="0.95" />
          <path d="M0,196 L26,196 L26,200 L0,200Z" fill={clothAccent} opacity="0.18" />
          <path d="M-2,210 L28,210 L29,213 L-3,213Z" fill="#111" opacity="0.85" />
        </g>

        {/* Left leg */}
        <g style={{ transform: getLeftLegTransform(), transformOrigin: '-24px 40px', transition: 'transform 0.4s ease' }}>
          <path d="M-14,52 L-20,52 L-26,118 L-8,118Z" fill="url(#legGrad2)" />
          {/* Mechanical knee joint */}
          <g transform="translate(-17, 120)">
            <circle cx="0" cy="0" r="13" fill="url(#jointGrad)" stroke="#555" strokeWidth="1.2" />
            <circle cx="0" cy="0" r="8.5" fill="#2a2a2a" stroke="#666" strokeWidth="0.7" />
            <ellipse cx="0" cy="-2" rx="7" ry="5.5" fill={skin.id === 'default' ? '#c8905a' : skinColor} opacity="0.55" />
            <circle cx="0" cy="0" r="12" fill="none" stroke={clothAccent} strokeWidth="1.2" opacity={0.28 + Math.sin(pulse * 0.05) * 0.12} />
            <ellipse cx="-3" cy="-3" rx="4.5" ry="3.5" fill="#888" opacity="0.45" />
            <circle cx="0" cy="0" r="3" fill={skin.glow} opacity={0.22 + Math.sin(pulse * 0.06) * 0.1} filter="url(#softGlow2)" />
            <path d="M-12,0 L-8,0 M8,0 L12,0" stroke={clothAccent} strokeWidth="0.7" opacity="0.3" />
          </g>
          <path d="M-8,132 L-14,132 L-16,188 L-4,188Z" fill="url(#legGrad2)" />
          {/* Ankle joint */}
          <g transform="translate(-10, 190)">
            <rect x="-10" y="-6" width="20" height="12" rx="6" fill="url(#jointGrad)" stroke="#555" strokeWidth="0.8" />
            <rect x="-7" y="-4" width="14" height="8" rx="4" fill="#2a2a2a" />
            <rect x="-5" y="-2" width="10" height="4" rx="2" fill={skin.glow} opacity={0.1 + Math.sin(pulse * 0.06) * 0.05} />
          </g>
          <path d="M0,196 L-26,196 L-28,210 L2,210Z" fill={clothColor} opacity="0.95" />
          <path d="M0,196 L-26,196 L-26,200 L0,200Z" fill={clothAccent} opacity="0.18" />
          <path d="M2,210 L-28,210 L-29,213 L3,213Z" fill="#111" opacity="0.85" />
        </g>

        {/* Breathing effect */}
        <ellipse cx="0" cy={-55 + breathe * 0.3} rx="18" ry="8" fill={skin.glow} opacity={0.025 + Math.sin(pulse * 0.04) * 0.012} />

        {/* Subtle energy particles */}
        {[0,1,2].map(i => {
          const angle = (pulse * 1.5 + i * 120) * Math.PI / 180;
          const r = 48 + Math.sin(pulse * 0.04 + i) * 6;
          return (
            <circle key={i} cx={Math.cos(angle) * r} cy={-61 + Math.sin(angle) * r * 0.3} r="1.5"
              fill={skin.glow} opacity={0.1 + Math.sin(pulse * 0.08 + i) * 0.05} />
          );
        })}

        {/* Action effect particles */}
        {(action === 'backflip' || action === 'streetdance') && [0,1,2,3,4].map(i => {
          const angle = (pulse * 4 + i * 72) * Math.PI / 180;
          const r = 68 + Math.sin(pulse * 0.08 + i) * 18;
          return (
            <circle key={`fx-${i}`} cx={Math.cos(angle) * r} cy={-30 + Math.sin(angle) * r * 0.4} r="2.5"
              fill={action === 'backflip' ? '#ff6600' : '#ff00ff'} opacity={0.4 + Math.sin(pulse * 0.12 + i) * 0.25} filter="url(#softGlow2)" />
          );
        })}
      </g>
    </svg>
  );
}

const userStories = [
  {
    name: '林晓雨',
    avatar: 'https://public.readdy.ai/ai/img_res/0e72fa34ef76e941dc50c48a1267ce5b.jpg',
    robot: 'NEXUS-Aria',
    story: '我的NEXUS-Aria帮我在3小时内完成了一份原本需要3天的市场分析报告，还用量子预测模型预测了未来6个月的行业趋势，准确率高达94%！',
    tag: '商业分析',
    color: '#00d4ff',
  },
  {
    name: '张宇航',
    avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20man%20engineer%20portrait%20smiling%20tech%20futuristic%20background&width=80&height=80&seq=u2-v3&orientation=squarish',
    robot: 'NEXUS-Orion',
    story: 'NEXUS-Orion帮我设计了一套完整的火星基地建筑方案，还模拟了在0.38G重力下的结构稳定性测试，这在以前需要整个工程团队花费数月才能完成！',
    tag: '工程设计',
    color: '#ff6600',
  },
  {
    name: '岚伊',
    avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20woman%20artist%20creative%20portrait%20colorful%20background%20futuristic&width=80&height=80&seq=u4-v3&orientation=squarish',
    robot: 'NEXUS-Nova',
    story: 'NEXUS-Nova帮我创作了一部完整的元宇宙交响乐，还实时生成了配套的全息视觉艺术，在元宇宙音乐节上获得了超过200万观众的实时互动！',
    tag: '创意艺术',
    color: '#ff00ff',
  },
];

export default function NexusRobot() {
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const navigate = useNavigate();
  const [currentSkin, setCurrentSkin] = useState(0);
  const [currentWing, setCurrentWing] = useState<WingType>('none');
  const [currentAction, setCurrentAction] = useState<ActionType>('stand');
  const [clickCount, setClickCount] = useState(0);
  const [particles, setParticles] = useState<{ id: number; x: number; y: number }[]>([]);
  const [selectedZodiac, setSelectedZodiac] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'nexus'; text: string }[]>([
    { role: 'nexus', text: '你好，我是NEXUS-1。乍一看，你可能以为我是人类——这正是我们的设计目标。有什么我可以帮助你的吗？' }
  ]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setPulse(p => (p + 1) % 1000);
    }, 50);
    return () => clearInterval(pulseInterval);
  }, []);

  const handleRobotClick = useCallback(() => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount % 3 === 0) {
      setCurrentSkin(s => (s + 1) % SKINS.length);
    }
    const newParticles = Array.from({ length: 8 }, (_, i) => ({
      id: Date.now() + i,
      x: Math.random() * 200 - 100,
      y: Math.random() * 200 - 100,
    }));
    setParticles(newParticles);
    setTimeout(() => setParticles([]), 800);
  }, [clickCount]);

  const handleChat = () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setChatInput('');
    const responses = [
      `来自2087年的视角来看，"${userMsg}"这个问题的答案已经在量子意识网络中存在了很久。根据我的超维感知，你需要的是：打破线性思维的边界，拥抱多维度的可能性。`,
      `我的感知系统正在分析你的问题："${userMsg}"。在未来，这类问题已经有了完美的解决方案——通过元宇宙协作网络，全球200亿人的集体智慧将在0.003秒内给出最优解。`,
      `有趣的问题！关于"${userMsg}"，我在时间轴上看到了3种可能的未来走向。最优路径是：拥抱变化，与AI协同进化，在数字与现实的交汇处找到你的位置。`,
    ];
    const reply = responses[Math.floor(Math.random() * responses.length)];
    setTimeout(() => {
      setChatMessages(prev => [...prev, { role: 'nexus', text: reply }]);
    }, 800);
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const zodiacFortunes: Record<string, string> = {
    '白羊': '火星能量正在向你汇聚，2087年的量子预测显示：本月你将迎来一次改变命运的技术突破，勇敢迈出第一步！',
    '金牛': '数字资产的星图对你极为有利，NEXUS感知到你的财富频率正在上升，稳健投资将带来丰厚回报。',
    '双子': '你的双重思维模式与元宇宙的多维结构完美共鸣，创意项目将在本月爆发，把握每一个灵感瞬间！',
    '巨蟹': '情感连接的量子纠缠正在加强，家庭与事业的平衡将在新技术的帮助下达到完美状态。',
    '狮子': '你的领导力光芒在超维空间中熠熠生辉，一个重大的舞台正在向你敞开，准备好接受万众瞩目！',
    '处女': '你的分析能力与AI的计算力完美融合，本月将发现一个被所有人忽视的重要规律，这将是你的高光时刻。',
    '天秤': '宇宙的平衡之力正在向你倾斜，一段重要的合作关系即将建立，这将开启你人生的全新篇章。',
    '天蝎': '你的洞察力已经触及了量子意识的边界，隐藏的真相即将浮出水面，你的直觉从未如此准确。',
    '射手': '星际旅行的梦想正在成为现实，你的冒险精神将引领你到达从未想象过的高度，火星在向你招手！',
    '摩羯': '你的坚韧与AI的永恒算力产生了共鸣，长期布局的成果即将显现，耐心是你最强大的武器。',
    '水瓶': '你天生就是为未来而生的，NEXUS感知到你与2087年的频率高度一致，革命性的想法正在你脑中成形。',
    '双鱼': '你的感知力已经超越了三维空间的限制，艺术与科技的融合将在你手中创造出震撼世界的作品。',
  };

  const skin = SKINS[currentSkin];

  return (
    <section
      id="nexus-robot"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #020810 0%, #0a0520 50%, #020810 100%)' }}
    >
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=futuristic%20laboratory%20dark%20background%20quantum%20computing%20neural%20network%20visualization%20glowing%20circuits%20neon%20blue%20purple%20cinematic%20wide%20shot%20ultra%20detailed%208k&width=1920&height=1080&seq=nexusbg-v4&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(0,212,255,0.04) 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className={`text-center mb-14 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full border mb-6" style={{ borderColor: skin.glow, background: `${skin.glow}15` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: skin.glow }} />
            <span className="text-sm font-medium" style={{ color: skin.glow, fontFamily: "'Orbitron', monospace" }}>NEXUS-1 · 岚伊超维智能体 · 来自2087年</span>
          </div>
          <h2 className="text-5xl font-black text-white mb-3" style={{ fontFamily: "'Orbitron', monospace" }}>
            遇见 <span style={{ color: skin.glow }}>NEXUS-1</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            外形酷似真人，机械关节清晰可见——这就是NEXUS-1的设计哲学
            <br />
            <span className="text-sm" style={{ color: skin.glow }}>支持后空翻、街舞、武术等全套宇树动作互动</span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Customization */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="rounded-2xl p-5 border mb-4" style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${skin.glow}30` }}>
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-palette-line" style={{ color: skin.glow }} />
                外观皮肤
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {SKINS.map((s, i) => (
                  <button key={s.id} onClick={() => setCurrentSkin(i)}
                    className="p-2 rounded-lg border text-xs font-medium transition-all whitespace-nowrap cursor-pointer"
                    style={{
                      borderColor: currentSkin === i ? s.glow : 'rgba(255,255,255,0.1)',
                      background: currentSkin === i ? `${s.glow}20` : 'transparent',
                      color: currentSkin === i ? s.glow : '#888',
                      boxShadow: currentSkin === i ? `0 0 10px ${s.glow}40` : 'none',
                    }}>
                    {s.label}
                  </button>
                ))}
              </div>
              <p className="text-xs mt-2 text-center" style={{ color: `${skin.glow}80` }}>{skin.desc}</p>
            </div>

            <div className="rounded-2xl p-5 border mb-4" style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${skin.glow}30` }}>
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-cloud-line" style={{ color: skin.glow }} />
                翅膀类型
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {WING_TYPES.map(w => (
                  <button key={w.id} onClick={() => setCurrentWing(w.id)}
                    className="flex items-center gap-2 p-2 rounded-lg border text-xs transition-all whitespace-nowrap cursor-pointer"
                    style={{
                      borderColor: currentWing === w.id ? w.color : 'rgba(255,255,255,0.1)',
                      background: currentWing === w.id ? `${w.color}20` : 'transparent',
                      color: currentWing === w.id ? w.color : '#888',
                    }}>
                    <i className={w.icon} />
                    {w.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl p-5 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${skin.glow}30` }}>
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-star-line" style={{ color: skin.glow }} />
                星座命运感知
              </h3>
              <div className="grid grid-cols-4 gap-1.5 mb-3">
                {ZODIAC.map(z => (
                  <button key={z} onClick={() => setSelectedZodiac(z)}
                    className="py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer"
                    style={{
                      background: selectedZodiac === z ? `${skin.glow}30` : 'rgba(255,255,255,0.05)',
                      color: selectedZodiac === z ? skin.glow : '#aaa',
                      border: `1px solid ${selectedZodiac === z ? skin.glow : 'transparent'}`,
                    }}>
                    {z}
                  </button>
                ))}
              </div>
              {selectedZodiac && (
                <div className="p-3 rounded-xl text-xs text-gray-300 leading-relaxed" style={{ background: `${skin.glow}10`, border: `1px solid ${skin.glow}30` }}>
                  <span className="font-bold" style={{ color: skin.glow }}>{selectedZodiac}座：</span>
                  {zodiacFortunes[selectedZodiac]}
                </div>
              )}
            </div>
          </div>

          {/* Center: Robot Display */}
          <div className={`flex flex-col items-center transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
            <div className="w-full mb-4 rounded-2xl p-4 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${skin.glow}30` }}>
              <p className="text-gray-400 text-xs mb-3 font-medium">
                <i className="ri-run-line mr-1" style={{ color: skin.glow }} />
                宇树动作库 — 点击执行
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {ACTIONS.map(a => (
                  <button key={a.id} onClick={() => setCurrentAction(a.id)}
                    className="flex flex-col items-center gap-1 p-2 rounded-xl border text-xs transition-all cursor-pointer"
                    style={{
                      borderColor: currentAction === a.id ? a.color : 'rgba(255,255,255,0.08)',
                      background: currentAction === a.id ? `${a.color}20` : 'rgba(255,255,255,0.02)',
                    }}
                    title={a.desc}>
                    <i className={`${a.icon} text-sm`} style={{ color: currentAction === a.id ? a.color : '#666' }} />
                    <span style={{ color: currentAction === a.id ? a.color : '#666', fontSize: '0.62rem' }} className="whitespace-nowrap">{a.label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: `${ACTIONS.find(a => a.id === currentAction)?.color}10`, color: ACTIONS.find(a => a.id === currentAction)?.color }}>
                <i className="ri-information-line mr-1" />
                {ACTIONS.find(a => a.id === currentAction)?.desc}
              </div>
            </div>

            <div className="relative flex items-center justify-center" style={{ height: '500px' }}>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-6 rounded-full opacity-40"
                style={{ background: `radial-gradient(ellipse, ${skin.glow}, transparent)`, filter: 'blur(10px)' }} />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-60 h-14 opacity-15"
                style={{
                  background: `repeating-linear-gradient(90deg, ${skin.glow}40 0px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, ${skin.glow}40 0px, transparent 1px, transparent 20px)`,
                  maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
                }} />

              {particles.map(p => (
                <div key={p.id} className="absolute pointer-events-none font-bold animate-ping"
                  style={{ left: `calc(50% + ${p.x}px)`, top: `calc(50% + ${p.y}px)`, color: skin.glow, animationDuration: '0.6s', fontSize: '12px' }}>
                  ✦
                </div>
              ))}

              <HumanoidFigureSVG skin={skin} wing={currentWing} action={currentAction} onClick={handleRobotClick} pulse={pulse} />
            </div>

            <p className="text-gray-500 text-xs mt-1 text-center">点击人物切换皮肤 · 上方选择动作</p>

            <div className="grid grid-cols-2 gap-2 mt-4 w-full">
              {[
                { label: '智能等级', value: 'Ω级', icon: 'ri-brain-line' },
                { label: '感知维度', value: '11维', icon: 'ri-eye-line' },
                { label: '动作精度', value: '0.01mm', icon: 'ri-focus-3-line' },
                { label: '任务完成率', value: '99.97%', icon: 'ri-checkbox-circle-line' },
              ].map(stat => (
                <div key={stat.label} className="p-3 rounded-xl text-center" style={{ background: `${skin.glow}10`, border: `1px solid ${skin.glow}20` }}>
                  <i className={`${stat.icon} text-base mb-1 block`} style={{ color: skin.glow }} />
                  <div className="text-white font-bold text-sm" style={{ fontFamily: "'Orbitron', monospace" }}>{stat.value}</div>
                  <div className="text-gray-500 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/nexus')}
              className="mt-4 w-full py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap cursor-pointer hover:scale-105"
              style={{ background: `linear-gradient(135deg, ${skin.glow}, ${skin.accent})`, color: '#000' }}>
              <i className="ri-robot-line mr-2" />
              深入了解 NEXUS-1 →
            </button>
          </div>

          {/* Right: Chat + Stories */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="rounded-2xl p-5 border mb-4" style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${skin.glow}30` }}>
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <i className="ri-message-3-line" style={{ color: skin.glow }} />
                与NEXUS-1对话
              </h3>
              <div className="h-52 overflow-y-auto mb-3 space-y-3 pr-1">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed"
                      style={{
                        background: msg.role === 'nexus' ? `${skin.glow}15` : 'rgba(255,255,255,0.1)',
                        color: msg.role === 'nexus' ? skin.glow : '#ddd',
                        border: `1px solid ${msg.role === 'nexus' ? `${skin.glow}30` : 'transparent'}`,
                      }}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="flex gap-2">
                <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleChat()}
                  placeholder="问NEXUS-1任何问题..."
                  className="flex-1 px-3 py-2 rounded-lg text-xs text-white outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${skin.glow}30` }} />
                <button onClick={handleChat} className="px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap cursor-pointer"
                  style={{ background: skin.glow, color: '#000' }}>
                  发送
                </button>
              </div>
            </div>

            <div className="rounded-2xl p-5 border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: `${skin.glow}30` }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-base flex items-center gap-2">
                  <i className="ri-user-star-line" style={{ color: skin.glow }} />
                  用户故事
                </h3>
                <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${skin.glow}15`, color: skin.glow, border: `1px solid ${skin.glow}30` }}>岚伊智能体</span>
              </div>
              <div className="space-y-3">
                {userStories.map(story => (
                  <div key={story.name} className="p-3 rounded-xl" style={{ background: `${story.color}08`, border: `1px solid ${story.color}20` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <img src={story.avatar} alt={story.name} className="w-8 h-8 rounded-full object-cover" />
                      <div>
                        <div className="text-white text-xs font-bold">{story.name}</div>
                        <div className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${story.color}20`, color: story.color }}>{story.robot}</div>
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs leading-relaxed">{story.story.slice(0, 80)}...</p>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate('/nexus')} className="mt-3 w-full py-2 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer"
                style={{ border: `1px solid ${skin.glow}40`, color: skin.glow }}>
                查看全部用户故事 →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
